<script setup lang="ts">
import { computed, useAttrs, ref } from 'vue';
import { BaseInputField } from '@/interfaces/InputFields';
import { useTextareaAutosize } from '@vueuse/core';
import { collapseElement } from '@/lib/collapse-element';

const props = defineProps<
  BaseInputField & {
    modelValue?: any;
    autofocus?: boolean;
    disabled?: boolean;
    expanded?: boolean;
    valid: boolean;
  }
>();

const attrs = useAttrs();
const internalExpaneded = ref(props.expanded);

const expandedTextArea = computed({
  get() {
    return props.expanded;
  },
  set(newValue) {
    emit('update:expanded', newValue);
    internalExpaneded.value = newValue;
  },
});

const emit = defineEmits(['update:modelValue', 'update:expanded']);

const inputValue = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

const { textarea, input } = useTextareaAutosize({
  input: inputValue,
});

const setTextareaRef = (el: HTMLTextAreaElement) => {
  if (!internalExpaneded.value) {
    textarea.value = undefined;
    collapseElement(el);
  } else {
    textarea.value = el;
  }
};

const focus = () => {
  if (textarea.value && typeof textarea.value.focus === 'function') {
    textarea.value?.focus();
  }
};

const onFocus = () => {
  expandedTextArea.value = true;
};

const onBlur = () => {
  setTimeout(() => {
    expandedTextArea.value = false;
  }, 250);
};

defineExpose({ focus });
</script>

<template>
  <textarea
    :id="name"
    :ref="setTextareaRef"
    v-model="input"
    :disabled="disabled"
    @focus="onFocus"
    @blur="onBlur"
    :required="required"
    :autofocus="autofocus"
    :autocomplete="label"
    :placeholder="(attrs.placeholder as string)"
    class="Field peer"
    :class="[
      `Field--${type}`,
      {
        'Field--invalid': !valid,
        'tw-transition-[height] tw-duration-500 tw-ease-in-out': !internalExpaneded,
      },
    ]"
  />
</template>
